"use client";

import React from "react";
import { Activity, Cpu, RefreshCw, Zap, Server, Wifi, ShieldCheck } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const phases = [{
  title: "CAD to 3D Transformation",
  description: "We ingest BIM, CAD, and GIS data to create millimeter-accurate structural models and digital twins.",
  icon: Wifi
}, {
  title: "Photorealistic Rendering",
  description: "Experience cinematic visuals with real-time lighting and physically-based materials for total immersion.",
  icon: Cpu
}, {
  title: "Spatial Intelligence",
  description: "Integrate live data layers for occupancy, traffic, and environmental monitoring within the twin.",
  icon: RefreshCw
}, {
  title: "Multi-Platform Delivery",
  description: "Deploy your digital twin seamlessly across web, mobile, and VR headsets with zero latency.",
  icon: Zap
}];
const hoverPoints = [{
  id: 1,
  x: "28%",
  y: "35%",
  title: "Smart Penthouse",
  metrics: [{
    label: "View Angle",
    value: "Panoramic",
    status: "normal"
  }, {
    label: "Material",
    value: "Premium",
    status: "normal"
  }]
}, {
  id: 2,
  x: "65%",
  y: "48%",
  title: "Commercial Plaza",
  metrics: [{
    label: "Occupancy",
    value: "85%",
    status: "warning"
  }, {
    label: "HVAC Status",
    value: "Optimal",
    status: "good"
  }]
}, {
  id: 3,
  x: "45%",
  y: "72%",
  title: "Foundation Core",
  metrics: [{
    label: "Structural",
    value: "Verified",
    status: "good"
  }, {
    label: "Sensors",
    value: "Active",
    status: "good"
  }]
}];
const stats = [{
  label: "Polygon Count",
  value: "10M+",
  description: "Optimized for web performance",
  icon: Activity
}, {
  label: "Global Coverage",
  value: "500+",
  description: "Successful urban deployments",
  icon: Server
}, {
  label: "System Uptime",
  value: "99.9%",
  description: "Reliable cloud streaming",
  icon: ShieldCheck
}];
export default function RealTimeMonitoring() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: isStatsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="200749" id="digital-twin-construction" className="bg-background text-foreground py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-700 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 font-default">
            Advanced Digital Twin Construction
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Building high-fidelity virtual replicas of your urban assets with surgical precision. Our pipeline transforms 
            complex architectural data into immersive, interactive environments.
          </p>
        </div>

        {}
        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20 transition-all duration-1000 delay-200 ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="relative">
            {}
            <div className="absolute left-[27px] top-8 bottom-8 w-px bg-border hidden sm:block" />

            <div className="space-y-10">
              {phases.map((phase, i) => <div key={i} data-index={i} className="relative flex gap-6 items-start group">
                  <div className="relative z-10 flex size-14 shrink-0 items-center justify-center rounded-full bg-card border border-border shadow-sm group-hover:border-primary group-hover:text-primary transition-colors duration-300">
                    <phase.icon className="size-6 text-primary" />
                  </div>
                  <div className="pt-3">
                    <h3 className="text-xl font-semibold mb-2 font-default">{phase.title}</h3>
                    <p className="text-muted-foreground leading-relaxed font-default">
                      {phase.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="relative rounded-xl border border-border bg-card shadow-2xl overflow-hidden aspect-square sm:aspect-video lg:aspect-square xl:aspect-[4/3]">
            {}
            <div className="h-12 border-b border-border bg-muted/50 flex items-center px-4 gap-2">
              <div className="flex gap-1.5">
                <div className="size-3 rounded-full bg-destructive/80" />
                <div className="size-3 rounded-full bg-chart-5/80" />
                <div className="size-3 rounded-full bg-chart-2/80" />
              </div>
              <div className="mx-auto h-6 w-1/3 bg-background rounded-md border border-border flex items-center justify-center">
                <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">PropVR Interface</span>
              </div>
            </div>

            {}
            <div className="relative w-full h-[calc(100%-3rem)] bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621178/img/real_time_dashboard_map.png" alt="Digital Twin Construction Interface" className="w-full h-full object-cover opacity-90 mix-blend-luminosity" />
              
              {}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

              {}
              {hoverPoints.map((point, i) => <HoverCard key={point.id} openDelay={200} closeDelay={100}>
                  <HoverCardTrigger asChild data-index={i}>
                    <button className="absolute size-5 rounded-full bg-primary shadow-[0_0_15px_rgba(23,85,253,0.6)] flex items-center justify-center cursor-pointer hover:scale-125 transition-transform duration-200 group" style={{
                  top: point.y,
                  left: point.x
                }} aria-label={`View details for ${point.title}`}>
                      <span className="absolute inset-0 rounded-full bg-primary animate-ping opacity-75 group-hover:animate-none" />
                      <span className="relative size-2 rounded-full bg-white" />
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-64 p-0 overflow-hidden border-border shadow-xl rounded-xl" align="center" sideOffset={10}>
                    <div className="bg-muted px-4 py-3 border-b border-border flex items-center gap-2">
                      <Activity className="size-4 text-primary" />
                      <span className="font-semibold text-sm font-default">{point.title}</span>
                    </div>
                    <div className="p-4 space-y-3 bg-card">
                      {point.metrics.map((metric, idx) => <div key={idx} className="flex justify-between items-center text-sm font-default">
                          <span className="text-muted-foreground">{metric.label}</span>
                          <span className={`font-mono font-medium ${metric.status === 'warning' ? 'text-chart-1' : metric.status === 'good' ? 'text-chart-2' : 'text-foreground'}`}>
                            {metric.value}
                          </span>
                        </div>)}
                    </div>
                  </HoverCardContent>
                </HoverCard>)}
            </div>
          </div>
        </div>

        {}
        <div ref={statsRef} className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-1000 delay-400 ${isStatsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {stats.map((stat, i) => <Card key={i} data-index={i} className="relative overflow-hidden border-border bg-card shadow-sm hover:shadow-md transition-shadow rounded-xl">
              {}
              <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
              
              <CardHeader className="pb-2 pt-6">
                <div className="flex items-center justify-between">
                  <CardDescription className="text-sm font-medium uppercase tracking-wider font-default">
                    {stat.label}
                  </CardDescription>
                  <stat.icon className="size-5 text-muted-foreground opacity-50" />
                </div>
                <CardTitle className="text-4xl font-bold tracking-tight mt-2 font-default">
                  {stat.value}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground font-default">
                  {stat.description}
                </p>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}